package com.arena_web.ui.theme

import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import kotlinx.browser.document
import org.jetbrains.compose.web.css.Color as CssColor
import org.jetbrains.compose.web.css.px

fun initAppTheme(ctx: InitSilkContext) {

    // Load Urbanist + IBM Plex Sans from Google Fonts
    val googleFontsLink = document.createElement("link") as org.w3c.dom.HTMLLinkElement
    googleFontsLink.rel = "stylesheet"
    googleFontsLink.href = "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;600&family=Urbanist:wght@300;400;500;600;700&display=swap"
    document.head?.appendChild(googleFontsLink)

    // Preconnect for faster font loading
    val preconnect = document.createElement("link") as org.w3c.dom.HTMLLinkElement
    preconnect.rel = "preconnect"
    preconnect.href = "https://fonts.googleapis.com"
    document.head?.insertBefore(preconnect, document.head?.firstChild)

    val preconnectGstatic = document.createElement("link") as org.w3c.dom.HTMLLinkElement
    preconnectGstatic.rel = "preconnect"
    preconnectGstatic.href = "https://fonts.gstatic.com"
    preconnectGstatic.setAttribute("crossorigin", "")
    document.head?.insertBefore(preconnectGstatic, document.head?.firstChild)

    // Global keyframe animations
    val styleEl = document.createElement("style")
    styleEl.textContent = """
        @keyframes arena-border-pulse {
            0%, 100% { border-color: rgba(0, 97, 255, 0.3); }
            50% { border-color: rgba(0, 97, 255, 0.8); }
        }
    """.trimIndent()
    document.head?.appendChild(styleEl)

    // Global responsive scale factor: 1 at 1920×1080, scales proportionally in both directions
    ctx.stylesheet.registerStyleBase(":root") {
        Modifier.styleModifier {
            property("--scale", "max(0.4px, min(calc(100vw / 1920), calc(100vh / 1080)))")
            // Mobile scale factor — 1 at the 390-px design baseline, scales
            // DOWN proportionally on narrower phones (e.g. 0.82 on a 320-px
            // device). Caps at 1 so wider mobile viewports don't blow up the
            // design. Used by raw-px mobile components via `m()` helpers.
            property("--mobile-scale", "min(1px, calc(100vw / 390))")
        }
    }

    // SVG noise texture to prevent color banding on near-black #02040B background
    val noiseSvg = buildString {
        append("<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'>")
        append("<filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/></filter>")
        append("<rect width='100%' height='100%' filter='url(%23n)' opacity='0.03'/></svg>")
    }
    val noiseDataUri = "url(\"data:image/svg+xml,${noiseSvg}\")"

    ctx.stylesheet.registerStyleBase("body") {
        Modifier
            .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
            .fontSize(16.px)
            .fontWeight(AppTypography.Regular)
            .margin(0.px)
            .padding(0.px)
            .overflow(Overflow.Hidden)
            .styleModifier {
                // Figma 10567:190904 — two-layer fill:
                //   • Base: solid #02040B at 100 %.
                //   • Top: linear gradient at 40 % layer opacity with a stop
                //     #3C79F1 18 % alpha at 64 %. Pre-multiplied alpha
                //     0.18 × 0.40 = 0.072 baked into the rgba below.
                // Noise SVG layered on top of the tint prevents banding.
                property("background-color", "#02040B")
                property(
                    "background-image",
                    "$noiseDataUri, " +
                        "linear-gradient(0deg, " +
                        "rgba(60, 121, 241, 0) 0%, " +
                        "rgba(60, 121, 241, 0.072) 64%, " +
                        "rgba(60, 121, 241, 0.072) 100%)"
                )
                property("background-repeat", "repeat, no-repeat")
            }
    }

    ctx.stylesheet.registerStyleBase("html") {
        Modifier
            .overflow(Overflow.Hidden)
            .backgroundColor(AppColors.Dark.Background)
    }

    // Make Silk's .silk-surface wrapper transparent so only body background shows
    // Must use !important to override inline style set by Silk's Surface composable
    ctx.stylesheet.registerStyleBase(".silk-surface") {
        Modifier.styleModifier {
            property("background-color", "transparent !important")
        }
    }

    ctx.stylesheet.registerStyleBase("input::placeholder, textarea::placeholder") {
        Modifier.styleModifier {
            property("color", "rgba(153, 177, 191, 0.5)")
            property("opacity", "1")
        }
    }

    // App-wide selection / drag suppression so the page reads like a native
    // app — no rogue text highlighting on long-press, no ghost previews when
    // dragging cards or avatars around. Inputs override this back to `text`
    // below so users can still edit form fields normally.
    ctx.stylesheet.registerStyleBase("body") {
        Modifier.styleModifier {
            property("user-select", "none")
            property("-webkit-user-select", "none")
            property("-moz-user-select", "none")
            property("-ms-user-select", "none")
            property("-webkit-touch-callout", "none")
        }
    }
    ctx.stylesheet.registerStyleBase("input, textarea, [contenteditable=\"true\"], [contenteditable=\"\"]") {
        Modifier.styleModifier {
            property("user-select", "text")
            property("-webkit-user-select", "text")
            property("-moz-user-select", "text")
            property("-ms-user-select", "text")
        }
    }
    ctx.stylesheet.registerStyleBase("img") {
        Modifier.styleModifier {
            property("-webkit-user-drag", "none")
            property("user-drag", "none")
            property("pointer-events", "auto")
            property("-webkit-touch-callout", "none")
        }
    }

    // Leaflet zoom controls — themed to match the app's dark surface.
    // Leaflet renders these inside the map container with its own CSS; we
    // override the relevant classes globally so every map looks consistent.
    ctx.stylesheet.registerStyleBase(".leaflet-bar") {
        Modifier.styleModifier {
            property("background", "rgba(11, 22, 40, 0.85)")
            property("backdrop-filter", "blur(8px)")
            property("-webkit-backdrop-filter", "blur(8px)")
            property("border", "0.5px solid rgba(0, 97, 255, 0.35)")
            property("border-radius", "12px")
            property("box-shadow", "0 6px 18px rgba(0, 0, 0, 0.35)")
            property("overflow", "hidden")
        }
    }
    ctx.stylesheet.registerStyleBase(".leaflet-bar a, .leaflet-bar a:link") {
        Modifier.styleModifier {
            property("background-color", "transparent")
            property("color", "#bed6f5")
            property("border-bottom", "0.5px solid rgba(255, 255, 255, 0.08)")
            property("width", "32px")
            property("height", "32px")
            property("line-height", "32px")
            property("font-size", "20px")
            property("font-weight", "500")
            property("text-decoration", "none")
        }
    }
    ctx.stylesheet.registerStyleBase(".leaflet-bar a:last-child") {
        Modifier.styleModifier {
            property("border-bottom", "none")
        }
    }
    ctx.stylesheet.registerStyleBase(".leaflet-bar a:hover, .leaflet-bar a:focus") {
        Modifier.styleModifier {
            property("background-color", "rgba(0, 97, 255, 0.18)")
            property("color", "#ffffff")
        }
    }
    ctx.stylesheet.registerStyleBase(".leaflet-bar a.leaflet-disabled") {
        Modifier.styleModifier {
            property("background-color", "transparent")
            property("color", "rgba(190, 214, 245, 0.3)")
            property("cursor", "default")
        }
    }
    // Hide Leaflet's bottom-right "Leaflet | OpenStreetMap" attribution chip —
    // it clashes with the dark theme and is not needed in our embedded views.
    ctx.stylesheet.registerStyleBase(".leaflet-control-attribution") {
        Modifier.styleModifier {
            property("background", "rgba(11, 22, 40, 0.7)")
            property("color", "rgba(190, 214, 245, 0.6)")
            property("border-radius", "6px 0 0 0")
            property("font-size", "10px")
        }
    }
    ctx.stylesheet.registerStyleBase(".leaflet-control-attribution a") {
        Modifier.styleModifier {
            property("color", "rgba(190, 214, 245, 0.85)")
        }
    }

    // Arena map marker popups — dark mini event cards (LeafletMap.addMarkers).
    // Leaflet's default popup chrome is white; restyle it to the app surface.
    ctx.stylesheet.registerStyleBase(".arena-map-popup .leaflet-popup-content-wrapper") {
        Modifier.styleModifier {
            property("background", "#060E1E")
            property("color", "#ffffff")
            property("border-radius", "16px")
            property("border", "0.5px solid rgba(0, 97, 255, 0.35)")
            property("box-shadow", "0 12px 32px rgba(0, 0, 0, 0.5)")
            property("padding", "0")
            property("overflow", "hidden")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-map-popup .leaflet-popup-content") {
        Modifier.styleModifier {
            property("margin", "0")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-map-popup .leaflet-popup-tip") {
        Modifier.styleModifier {
            property("background", "#060E1E")
            property("border", "0.5px solid rgba(0, 97, 255, 0.35)")
        }
    }
    // Event card strip floating over the Arena map — hide its scrollbar.
    ctx.stylesheet.registerStyleBase("#arena-map-cards::-webkit-scrollbar") {
        Modifier.styleModifier {
            property("display", "none")
        }
    }

    // Arena map info-card pins (LeafletMap.addMarkers divIcon) — the pin IS a
    // mini event card: photo + date/time + title with a tail at the venue.
    ctx.stylesheet.registerStyleBase(".arena-pin") {
        Modifier.styleModifier {
            property("background", "none")
            property("border", "none")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-pin-wrap") {
        Modifier.styleModifier {
            property("position", "absolute")
            property("left", "0")
            property("top", "0")
            // Origin at the tail tip keeps the pin anchored to its venue when
            // the zoom-driven scale (set by LeafletMap.initReadOnly) changes.
            property("transform-origin", "50% 100%")
            property("transform", "translate(-50%, -100%) scale(var(--arena-pin-scale, 1))")
            property("transition", "transform 0.15s ease-out")
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
            property("cursor", "pointer")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-pin-card") {
        Modifier.styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("justify-content", "flex-end")
            property("gap", "1px")
            property("width", "104px")
            property("height", "58px")
            property("padding", "5px 6px")
            property("box-sizing", "border-box")
            // Photo (when present) layered under the scrim via the inline
            // background-image stack; navy base shows when there is no photo.
            property("background-color", "#0F172A")
            property("background-size", "cover")
            property("background-position", "center")
            property("border", "1px solid rgba(0, 97, 255, 0.45)")
            property("border-radius", "12px")
            property("box-shadow", "0 8px 20px rgba(0, 0, 0, 0.45)")
            property("overflow", "hidden")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-pin-datetime") {
        Modifier.styleModifier {
            property("font-size", "9px")
            property("font-weight", "600")
            property("color", "#CFE0FF")
            property("white-space", "nowrap")
            property("overflow", "hidden")
            property("text-overflow", "ellipsis")
            property("text-shadow", "0 1px 2px rgba(0, 0, 0, 0.6)")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-pin-title") {
        Modifier.styleModifier {
            property("font-size", "11px")
            property("font-weight", "500")
            property("color", "#ffffff")
            property("overflow", "hidden")
            property("text-overflow", "ellipsis")
            property("white-space", "nowrap")
            property("text-shadow", "0 1px 2px rgba(0, 0, 0, 0.6)")
        }
    }
    ctx.stylesheet.registerStyleBase(".arena-pin-tip") {
        Modifier.styleModifier {
            property("width", "10px")
            property("height", "10px")
            property("background", "rgba(6, 14, 30, 0.95)")
            property("transform", "rotate(45deg)")
            property("margin-top", "-6px")
        }
    }
}
